/**
 * Le rendu du markdown limité (docs/13 §6).
 *
 * Un composant par nœud de `lib/markdown`, et **aucun chemin vers du HTML** :
 * React échappe le texte, l'arbre ne contient que les cinq nœuds déclarés, et
 * un nœud inconnu ne peut pas exister — c'est le type qui le dit, pas une
 * vérification. C'est la même construction que `RichContent` côté catalogue,
 * pour la même raison.
 */
import type { ReactNode } from "react";

import { ExternalLink } from "@/features/messaging/components/ExternalLink";
import { parseMessage, type Inline } from "@/features/messaging/lib/markdown";

function renderInline(nodes: Inline[]): ReactNode {
  return nodes.map((node, index) => {
    if (node.kind === "text") return <span key={index}>{node.text}</span>;
    if (node.kind === "bold") return <strong key={index}>{renderInline(node.children)}</strong>;
    if (node.kind === "italic") return <em key={index}>{renderInline(node.children)}</em>;
    return <ExternalLink key={index} href={node.href} />;
  });
}

export function MessageMarkdown({ body }: { body: string }) {
  return (
    <>
      {parseMessage(body).map((block, index) => {
        if (block.kind === "paragraph") {
          // `whitespace-pre-wrap` : les sauts de ligne d'un paragraphe sont
          // conservés dans le texte, et c'est le style qui les rend.
          return (
            <p key={index} className="whitespace-pre-wrap">
              {renderInline(block.children)}
            </p>
          );
        }
        if (block.kind === "bulletList") {
          return (
            <ul key={index} className="list-disc space-y-0.5 pl-5">
              {block.items.map((item, itemIndex) => (
                <li key={itemIndex}>{renderInline(item)}</li>
              ))}
            </ul>
          );
        }
        return (
          <ol key={index} className="list-decimal space-y-0.5 pl-5">
            {block.items.map((item, itemIndex) => (
              <li key={itemIndex}>{renderInline(item)}</li>
            ))}
          </ol>
        );
      })}
    </>
  );
}
